// Что какой раздел «Моделей» показывает поверх своей заливки.
//
// Правила вынесены из ModelsPage в чистые функции, потому что до 11.09.2026 они
// жили там полутора десятками условий `model === 'currents'`, разбросанными по
// эффектам, панели и формам. Косяки и ползунок дней переехали ещё и в
// «Комфортную среду» (замечания пользователей 11.09.2026), и разнести это
// «ещё и» по всем местам руками значило бы однажды забыть одно из них: косяк
// на карте есть, а карточки у него нет, или ползунок двигает дату, которой
// никто не читает.

import type { DriftCollection } from '../api'
import type { ModelKind } from './ModelPicker'
import { SCHOOL_SOURCE } from './schoolsVisibility'

/** Ползунок дней −7…+2 внизу карты. «Комфортной среде» он нужен так же, как
 *  «Дрейфам»: растр HSI и косяки на ней живут той же выбранной датой. У
 *  «Апвеллинга» внизу свои панели. */
export function showsDayTimeline(model: ModelKind): boolean {
  return model === 'currents' || model === 'hsi'
}

/** Показываются ли в разделе вручную отмеченные косяки (и их снос). */
export function schoolsShownIn(model: ModelKind): boolean {
  return model === 'currents' || model === 'hsi'
}

/** Ставится ли косяк кликом по карте.
 *
 *  Только в «Дрейфах»: там клик — разметка, жест в ходу давно. В «Комфортной
 *  среде» клик открывает отсчёт в точке — отобрать его значило бы лишить
 *  раздел карточки оценки ради второго места, где ставят то же самое. */
export function clusterCreateAllowed(model: ModelKind): boolean {
  return model === 'currents'
}

/** Нужен ли разделу снос из `/currents/drift`.
 *
 *  «Дрейфы» и есть снос. «Комфортной среде» он нужен только ради косяков:
 *  выключенные — запрос за треками, которых не покажут, был бы работой даром. */
export function driftFetchedIn(
  model: ModelKind, schools: boolean, catchShown = false,
): boolean {
  if (model === 'currents') return true
  if (model === 'hsi') return schools || catchShown
  return false
}

/** Снос, который раздел рисует.
 *
 *  «Комфортной среде» по умолчанию — только КОСЯКИ: треки операций поверх
 *  заливки индекса превратили бы её карту в карту «Дрейфов». Вылов Ф58 там
 *  включается тумблером «Исторический вылов» (`catchShown`). «Дрейфы»
 *  получают коллекцию целиком — ТОЙ ЖЕ ссылкой, чтобы не сбрасывать мемоизацию
 *  слоя на каждом рендере. */
export function driftForSection(
  fc: DriftCollection | null,
  model: ModelKind,
  catchShown = false,
): DriftCollection | null {
  if (!fc || model !== 'hsi' || catchShown) return fc
  return { ...fc, features: fc.features.filter(f => f.properties.source === SCHOOL_SOURCE) }
}

/** Гаснет ли слайдер глубины в разделе «Комфортная среда» (HSI).
 *
 *  Только здесь и только у поверхностного вида (решение владельца 13.10.2026):
 *  его HSI один на все горизонты, и слайдер, двигающийся без изменений карты,
 *  читается как поломка. Температуру, кислород и прочие поля смотрят на разных
 *  глубинах при любом виде — прочие разделы слайдер не трогают. Вид не найден
 *  в каталоге — слайдер остаётся: гасить по незнанию нельзя. */
export function hsiDepthOff(
  model: ModelKind,
  species: number | null,
  catalog: readonly { species_id: number; surface?: boolean }[] | undefined,
): boolean {
  if (model !== 'hsi' || species == null || !catalog) return false
  return catalog.find(s => s.species_id === species)?.surface === true
}

/** Разделы, которым нужно ПРОШЛОЕ: «Дрейфы» — это снос из прошлых суток и
 *  усики HSI с недельной обратной адвекцией. На домене без истории (selara,
 *  aquaculture.lv; решение владельца 20.10.2026) сервер на них отвечает
 *  `history_forbidden`, и раздел, всегда показывающий отказ, хуже его отсутствия. */
const NEEDS_HISTORY: ReadonlySet<ModelKind> = new Set<ModelKind>(['currents'])

/** Доступен ли раздел домену. */
export function sectionAllowed(model: ModelKind, history: boolean): boolean {
  return history || !NEEDS_HISTORY.has(model)
}

/** Раздел, который показывать: недоступный уводит на «Комфортную среду».
 *  Ссылка `?model=currents` на домене без истории открывается ей же. */
export function allowedSection(model: ModelKind, history: boolean): ModelKind {
  return sectionAllowed(model, history) ? model : 'hsi'
}
